import { createContext, type ReactNode, use, useEffect, useMemo, useState } from 'react';

import type { LegalLocale } from './legal/documents';

const STORAGE_KEY = 'carcarna-public-locale';

interface LocaleValue {
  locale: LegalLocale;
  setLocale: (locale: LegalLocale) => void;
  dir: 'rtl' | 'ltr';
}

const LocaleContext = createContext<LocaleValue | null>(null);

function storedLocale(): LegalLocale {
  try {
    const value = globalThis.localStorage.getItem(STORAGE_KEY);
    return value === 'en' ? 'en' : 'ar';
  } catch {
    return 'ar';
  }
}

export function PublicLocaleProvider({
  children,
  initialLocale,
}: {
  children: ReactNode;
  initialLocale?: LegalLocale;
}) {
  const [locale, setLocaleState] = useState<LegalLocale>(initialLocale ?? storedLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr';
  }, [locale]);

  const setLocale = (next: LegalLocale) => {
    setLocaleState(next);
    try {
      globalThis.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Hardened browsers may block storage; the live choice still works.
    }
  };

  const value = useMemo<LocaleValue>(
    () => ({ locale, setLocale, dir: locale === 'ar' ? 'rtl' : 'ltr' }),
    [locale],
  );

  return <LocaleContext value={value}>{children}</LocaleContext>;
}

export function usePublicLocale(): LocaleValue {
  const value = use(LocaleContext);
  if (!value) {
    throw new Error('usePublicLocale must be used inside PublicLocaleProvider.');
  }
  return value;
}
